import {DashboardWidget, WidgetErrorBoundary} from '@hconnect/common/components'
import {useUpmStructure} from '@hconnect/common/hooks'
import {Box, GridProps, LinearProgress, MenuItem, TextField} from '@mui/material'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {KMS_WIDGET_NAMESPACE} from '../../../consts'
import {useGetTelemetry, useGetUpmSignalsMultiple} from '../hooks'
import {SignalMetadata} from '../types'

import {AssetHealthSignalItem} from './AssetHealthSignalItem'
import {NoAlarms, NoTelemetryData} from './EmptyStates'

export interface AssetHealthProps {
  plantUpmId: string
  gridProps?: Omit<GridProps, 'item' | 'zeroMinWidth'>
  onNavigateToSignals?: () => void
  onNavigateToSignal?: (localName: string) => void
}

const defaultGridProps: Omit<GridProps, 'item' | 'zeroMinWidth'> = {xs: 12, md: 12, lg: 6}

const AssetHealthContent = ({
  plantUpmId,
  gridProps = defaultGridProps,
  onNavigateToSignals,
  onNavigateToSignal
}: AssetHealthProps) => {
  const {t} = useTranslation(KMS_WIDGET_NAMESPACE)
  const [areaFilter, setAreaFilter] = useState<string>('all')

  const {data: plantStructure, isPending: plantStructurePending} = useUpmStructure({
    plantId: plantUpmId
  })

  // all upm areas
  const areas = useMemo(() => plantStructure?.children || [], [plantStructure])

  // fetch signals for all areas
  const signalQueries = useGetUpmSignalsMultiple(
    areas.map(
      (area) =>
        ({
          entity: 'area',
          entityId: area.upmId
        }) as const
    )
  )
  const signalQueriesPending = signalQueries.some((query) => query.isPending)

  // get signal metadata to display in list
  const signalsMetadata = useMemo(() => {
    const metadata: {[key: string]: SignalMetadata} = {}

    signalQueries.forEach((query) => {
      if (query.data) {
        query.data.forEach(
          (signal) =>
            (metadata[signal.localName] = {
              localName: signal.localName,
              description: signal.description,
              area: signal.area.name,
              productionLine: signal.productionLine.name,
              source: signal.source,
              equipment: signal.equipment.name,
              svgId: ''
            })
        )
      }
    })
    return metadata
  }, [signalQueries])

  // get all local names per area
  const localNamesPerArea = useMemo(
    () =>
      areas.map((area, index) => ({
        id: area.upmId,
        signals: signalQueries[index]?.data?.map((signal) => signal.localName) || []
      })),
    [areas, signalQueries]
  )

  // get all local names from all areas
  const allLocalNames = useMemo(
    () => localNamesPerArea.reduce<string[]>((acc, area) => [...acc, ...area.signals], []),
    [localNamesPerArea]
  )

  // get signals for filtered area
  const filteredLocalNames = useMemo(() => {
    if (areaFilter === 'all') {
      return allLocalNames
    }
    return localNamesPerArea.find((area) => area.id === areaFilter)?.signals || []
  }, [areaFilter, localNamesPerArea, allLocalNames])

  // fetch last values for relevant signals
  const {
    data: telemetryData,
    isPending: telemetryPending,
    isFetching
  } = useGetTelemetry(plantUpmId, filteredLocalNames, {
    enabled: filteredLocalNames.length > 0 && !signalQueriesPending
  })

  // Get unhealthy signals
  const unhealthySignals = useMemo(() => {
    if (filteredLocalNames.length === 0) {
      return []
    }
    return (
      telemetryData?.filter(
        (signal) =>
          !!signal.lastModelBreach?.dateTime ||
          signal.threshold.status === 'Warning' ||
          signal.threshold.status === 'Alarm'
      ) || []
    )
  }, [filteredLocalNames.length, telemetryData])

  const isLoading = plantStructurePending || signalQueriesPending || telemetryPending

  const noData = !isLoading && telemetryData?.length === 0
  const noAlarms = !isLoading && unhealthySignals.length === 0

  return (
    <DashboardWidget
      title={t('assetHealth.title')}
      gridProps={gridProps}
      loading={isLoading}
      maxHeight={400}
      minWidth={330}
      onClick={onNavigateToSignals}
      headerAction={
        <TextField
          id="area-select"
          select
          label={t('assetHealth.areaLabel')}
          variant="filled"
          sx={{width: 200}}
          value={areaFilter}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setAreaFilter(e.target.value)}
        >
          <MenuItem value="all">{t('assetHealth.allAreas')}</MenuItem>
          {areas.map((area) => (
            <MenuItem key={area.upmId} value={area.upmId}>
              {area.type?.name}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {isFetching ? <LinearProgress /> : <Box sx={{height: '4px'}} />}
      {noData && <NoTelemetryData />}
      {!noData && noAlarms && <NoAlarms />}
      {!noData && !noAlarms && (
        <Box pr={1}>
          {unhealthySignals.map((signal) => (
            <AssetHealthSignalItem
              key={signal.localName}
              signal={signal}
              signalMetadata={signalsMetadata[signal.localName]}
              onClick={() => onNavigateToSignal?.(signal.localName)}
            />
          ))}
        </Box>
      )}
    </DashboardWidget>
  )
}

export const AssetHealth = ({gridProps = defaultGridProps, ...props}: AssetHealthProps) => (
  <WidgetErrorBoundary gridProps={gridProps}>
    <AssetHealthContent gridProps={gridProps} {...props} />
  </WidgetErrorBoundary>
)
